import { EventInput } from '@fullcalendar/common';
import { addDays } from '@fullcalendar/react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { ChangeEvent } from 'react';
import { useState } from 'react';

import { useEventMaterials } from '../../hooks/api/event-material/useEventMaterials';
import { useMaterials } from '../../hooks/api/materials/useMaterials';
import { EventMaterial, MaterialRequestQuantityPerDay } from '../../hooks/api/utils/types';
import { getCompanies } from '../../services/api/companies';
import { getRegistrations } from '../../services/api/registrations';
import BasicSelect from './fields/BasicSelect';
import CustomTypeahead from './fields/CustomTypeahead';
import {
  FullCalendarNew,
  addDaysRobust,
  dateInputToDate,
  extractDaysFromFullCalendarEvent,
  isSameDay,
  isSameDayIgnoreTZ,
} from './fields/FullCalendarNew';

export type MaterialRequestFormProps = {
  admin: boolean;
  onSubmit: (values: any) => void;
  event: any;
  registration: any;
};

type MaterialRequestPeriod = {
  start: Date;
  end: Date;
  endInclusive: Date;
  quantity: number;
};

export const MaterialRequestForm = (props: MaterialRequestFormProps) => {
  const [requests, setRequests] = useState<MaterialRequestPeriod[]>([]);
  const [companies, setCompanies] = useState<any>([]);

  const { data: materials, isLoading } = useMaterials();

  if (isLoading || !materials) {
    return <div>Loading...</div>;
  }

  return (
    <Formik
      initialValues={{
        materialId: '',
        requests: [],
        registrationId: props.admin ? '' : props.registration.id,
        forced: !!props.admin,
      }}
      validate={(values) => {
        const errors: any = {};
        if (props.admin) {
          if (!values?.registrationId) {
            errors.registrationId = 'required';
          }
        }

        if (!values?.materialId) {
          errors.materialId = 'required';
        }

        if (!values?.requests?.length) {
          errors.requests = 'required';
        }
        return errors;
      }}
      onSubmit={async (values, { resetForm }) => {
        await props.onSubmit(values);
        resetForm();
      }}
    >
      {(formikProps) => {
        const materialId = formikProps.values['materialId'] ? Number(formikProps.values['materialId']) : undefined;
        const { data: eventMaterials, isLoading: isLoadingEventMaterials } = useEventMaterials(
          props.event.id,
          materialId,
          {
            // always enabled
            // enabled: props.admin ?? false
          }
        );

        const setPeriodValue = (value: any) => formikProps.setFieldValue('requests', value);
        const setRegistrationId = (value: any) => formikProps.setFieldValue('registrationId', value);

        // triggered when the user drags one or more dates on the calendar
        const handleDateSelection = (event: EventInput) => {
          if (!materialId) {
            return;
          }

          if (
            requests.find((r) => {
              return (
                dateInputToDate(event?.start).getTime() >= r.start.getTime() &&
                dateInputToDate(event?.start).getTime() <= r.endInclusive.getTime()
              );
            })
          ) {
            return;
          }

          requests.push({
            quantity: 1,
            start: dateInputToDate(event.start),
            end: dateInputToDate(event.end),
            // end: addDays(dateInputToDate(event.end), 1),
            endInclusive: addDaysRobust(event.end, -1),
          });
          setRequests(requests);
          setPeriodValue(requests);
        };

        const handleDelete = (day: Date | string) => {
          const newRequests = requests.filter((r) => r.start !== day);
          setRequests(newRequests);
          setPeriodValue(newRequests);
        };

        const handleUpdate = (event: ChangeEvent<HTMLInputElement>, day: Date, field: keyof MaterialRequestPeriod) => {
          const requestToUpdate = requests.find((r) => r.start === day);
          if (requestToUpdate) {
            // @ts-ignore
            requestToUpdate[field] = event.currentTarget.value;
          }

          setRequests(requests);
          setPeriodValue(requests);
        };

        const handleNewCompany = (values: any) => {
          if (values.length > 0) {
            getRegistrations({
              'filter[company_id]': values[0].id,
              'filter[event_id]': props.event.id,
            }).then((response) => {
              if (response.data.length > 0) {
                setRegistrationId(response.data[0].id);
              }
            });
          }
        };

        const handleCompanySearch = (value: any) => {
          getCompanies({
            'filter[name]': value,
          }).then((response) => {
            setCompanies(response.data);
          });
        };

        const event = props.admin ? props.event : props.registration.event;

        let validRange: any = undefined;
        if (event.startsAt) {
          validRange = {
            start: event.startsAt.split(' ')[0],
            end: event.endsAt.split(' ')[0],
          };
        }

        const calEvents = constructCalEvents(
          requests,
          materialId !== undefined && !isLoadingEventMaterials ? eventMaterials?.data : [],
          new Date(event.startsAt.split(' ')[0]),
          new Date(event.endsAt.split(' ')[0]),
          props.admin
        );

        const calendarDisabled = !materialId || !formikProps.values?.registrationId || isLoadingEventMaterials;

        return (
          <Form className="m-login__form m-form" onSubmit={formikProps.handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-xl-6">
                  <BasicSelect
                    handleChange={(e: any) => {
                      return formikProps.handleChange(e);
                    }}
                    handleBlur={formikProps.handleBlur}
                    touched={formikProps.touched}
                    errors={formikProps.errors}
                    values={formikProps.values}
                    name="materialId"
                    label="Material"
                    placeholder="Select a material"
                    options={materials?.data?.map((material) => {
                      return {
                        value: material.id,
                        title: material.name,
                      };
                    })}
                  />

                  {props.admin && (
                    <CustomTypeahead
                      id="lkjeqwljkqrejkl"
                      name="registrationId"
                      label="Company"
                      multiple={false}
                      options={companies}
                      placeholder={'Choose a company'}
                      labelKey="name"
                      onSearch={handleCompanySearch}
                      onChange={handleNewCompany}
                      values={formikProps.values}
                      touched={formikProps.touched}
                      errors={formikProps.errors}
                      handleBlur={formikProps.handleBlur}
                    />
                  )}

                  {requests.length > 0 && (
                    <div className="form-group m-form__group">
                      <label>Overview</label>
                      <table className="table m-table m-table--head-separator-primary">
                        <thead>
                          <tr>
                            <th>From</th>
                            <th>Until</th>
                            <th>Amount</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {requests.map((event) => {
                            const start = moment(event.start);
                            const end = moment(event.endInclusive);
                            const capacityExceeded = hasExceededCapacity(event, eventMaterials?.data);

                            return (
                              <tr
                                key={event.start.toISOString()}
                                style={capacityExceeded ? { backgroundColor: 'rgba(255,0,0,0.2)' } : {}}
                              >
                                <td>{start.format('DD/MM/YY')}</td>
                                <td>{end.format('DD/MM/YY')}</td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control m-input"
                                    min="1"
                                    onChange={(e) => handleUpdate(e, event.start, 'quantity')}
                                    value={event.quantity}
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger m-btn m-btn--icon m-btn--icon-only"
                                    onClick={(e) => handleDelete(event.start)}
                                  >
                                    <i className="la la-trash" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="col-xl-6">
                  <div className="form-group m-form__group">
                    <label>
                      Availability{' '}
                      {calendarDisabled
                        ? props.admin
                          ? '(select Material and Company first)'
                          : 'select Material first'
                        : ''}
                    </label>
                    <FullCalendarNew
                      validRange={validRange}
                      selectable
                      editable
                      select={handleDateSelection}
                      eventOverlap={(stillEvent, movingEvent) => {
                        return stillEvent.display === 'background';
                      }}
                      // dateClick={(...args) => {
                      //   console.log("dateClick", args);
                      // }}
                      // eventDragStop={(...args) => {
                      //   console.log("eventDrag", args);
                      // }}
                      // eventAdd={(...args) => {
                      //   console.log("eventAdd", args);
                      // }}
                      // eventClick={(...args) => {
                      //   console.log("eventClick", args);
                      // }}
                      // eventChange={(...args) => {
                      //   console.log("eventChange", args);
                      // }}
                      // handleSelect={handleDateSelection}
                      // handleEdit={handleDateEdit}
                      events={calEvents}
                      disabled={calendarDisabled}
                    />
                    {/*<FullCalendar*/}
                    {/*  validRange={validRange}*/}
                    {/*  selectable*/}
                    {/*  editable*/}
                    {/*  handleSelect={handleDateSelection}*/}
                    {/*  handleEdit={handleDateEdit}*/}
                    {/*  events={calEvents}*/}
                    {/*  settingsOverrides={{ eventTextColor: 'red' }}*/}
                    {/*/>*/}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="row align-items-center">
                <div className="col-lg-12 m--align-right">
                  <button
                    className={
                      'btn m-btn btn-primary' +
                      (formikProps.isSubmitting ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
                    }
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

function constructCalEvents(
  periods: MaterialRequestPeriod[],
  eventMaterials: EventMaterial[] | undefined,
  start: Date, // start date of calendar
  end: Date, // end date of calendar
  isAdmin?: boolean
): EventInput[] {
  const calEvents: EventInput[] = periods.map(matRequestToEventInput);

  Array.from({ length: daysBetweenDates(start, end) }).forEach((_, index) => {
    const day = addDays(start, index);

    const eventMaterial = eventMaterials?.find(
      (e) => dateInputToDate(e.day).getTime() === addDays(start, index).getTime()
    );

    const remaining = (eventMaterial?.amount ?? 0) - (eventMaterial?.amountRequested ?? 0);

    const event: EventInput = {
      title: 'max ' + (isAdmin ? remaining : remaining < 0 ? 0 : remaining), // only show 0 for non-admins
      // @ts-ignore
      start: day,
      endInclusive: day,
      end: addDays(day, 1),
      allDay: true,
      myId: eventMaterial?.id + '',
      amount: eventMaterial?.amount ?? 0,
      editable: false,
      startEditable: false,
      durationEditable: false,
      resourceEditable: false,
      display: 'background',
      classNames: ['fc-event-bg-capacity'],
    };

    if (
      calEvents.find((request) => {
        return (
          dateInputToDate(request.start).getTime() <= dateInputToDate(day).getTime() &&
          dateInputToDate(request.end).getTime() >= dateInputToDate(day).getTime() &&
          request.quantity > remaining
        );
      })
    ) {
      event!.classNames = [
        // @ts-ignore
        ...event.classNames,
        'fc-event-bg-capacity-exceeded',
      ];
    } else if (remaining > 0) {
      event!.classNames = [
        // @ts-ignore
        ...event.classNames,
        'fc-event-bg-capacity-available',
      ];
    }
    calEvents.push(event);
  });

  // @ts-ignore
  return calEvents;
}

export function eventsToMaterialRequests(events: EventInput[], materialId: string): MaterialRequestQuantityPerDay[] {
  return events.reduce((requests, event) => {
    // split the event into days
    const days = extractDaysFromFullCalendarEvent(event);
    days.forEach((day) => {
      requests.push({
        quantity: Number(event.title),
        // get the date string from the iso string
        day: day,
      });
    });

    return requests;
  }, [] as MaterialRequestQuantityPerDay[]);
}

export function matRequestToEventInput(matRequestPeriod: MaterialRequestPeriod): EventInput {
  return {
    ...matRequestPeriod,
    title: matRequestPeriod.quantity + ' ',
    allDay: true,
    myId: matRequestPeriod.start.getTime(),
    editable: false,
  };
}

export function daysBetweenDates(start: Date, end: Date): number {
  return (end.getTime() - start.getTime()) / (60 * 1000 * 60 * 24);
}

function hasExceededCapacity(event: MaterialRequestPeriod, eventMaterials: EventMaterial[] | undefined): boolean {
  // for each day in the event input, check if the event has exceeded capacity
  const days = extractDaysFromFullCalendarEvent(event);
  return days.some((day) => {
    const eventMaterial = eventMaterials?.find((e) => {
      return isSameDayIgnoreTZ(e.day, day);
    });
    const remaining = (eventMaterial?.amount ?? 0) - (eventMaterial?.amountRequested ?? 0);

    return remaining < event.quantity;
  });
}
